import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';

interface InviteHotelAdminProps {
  open: boolean;
  onClose: () => void;
  hotelId: string;
  onInvited?: () => void;
}

interface HotelSummary {
  documentId: string;
  name: string;
  longName: string;
}

export default function InviteHotelAdmin({ open, onClose, hotelId, onInvited }: InviteHotelAdminProps) {
  const [hotel, setHotel] = useState<HotelSummary | null>(null);
  const [loadingHotel, setLoadingHotel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const { setSnackbarInput } = useApp();

  const isFormComplete = useMemo(() => {
    return firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().includes('@');
  }, [firstName, lastName, email]);

  useEffect(() => {
    if (!open || !hotelId) {
      return;
    }

    const fetchHotel = async () => {
      try {
        setLoadingHotel(true);
        setFormError('');

        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${APIHOST}/api/hotels/${hotelId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load hotel (${response.status})`);
        }

        const payload = await response.json();
        setHotel(payload?.data || null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load hotel';
        setFormError(message);
      } finally {
        setLoadingHotel(false);
      }
    };

    fetchHotel();
  }, [open, hotelId]);

  const handleInvite = async () => {
    if (!isFormComplete) {
      setFormError('First name, last name, and a valid email are required.');
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/hotels/${hotelId}/admin-invite`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
          },
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to invite hotel admin (${response.status})`);
      }

      setSnackbarInput({
        message: 'Invitation sent successfully',
        severity: 'success',
      });

      setFirstName('');
      setLastName('');
      setEmail('');
      if (onInvited) {
        onInvited();
      }
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to invite hotel admin';
      setFormError(message);
      setSnackbarInput({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite Hotel Administrator</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {loadingHotel ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hotel: {hotel?.longName || hotel?.name || hotelId}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleInvite} variant="contained" disabled={!isFormComplete || submitting || loadingHotel}>
          {submitting ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}